import axios from 'axios'
import { slsClient } from 'clients/slsClient'
import { useMountedState } from 'hooks/useMountedState'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Dispatch, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getUserData } from 'redux/reducers/users'
import { Logger } from 'utils/logger'

export interface UseUpdateAccountHandlerProps<T> {
  onClose: () => void,
  trackSLSActivity: (respStatus: string) => void
  transformFormDataToRequestData: (formData: T) => Parameters<typeof slsClient.updateAccountDetails>[0]['updates']
  /** Callback for when an update is successful. This should make any necessary calls to redux */
  handleSuccessfulUpdate: (dispatch: Dispatch<any>, formData: T) => void
}

export type UpdateAccountServerErrors = {
  basic?: boolean,
  invalidCriteria?: boolean
}

/**
 * This hook relies on launch darkly flags and should ideally only be used in areas where flags are loaded for sure (LaunchDarklyDependentContent)
 * @param props
 * @returns
 */
export const useUpdateAccountHandler = <T>({ onClose, trackSLSActivity, transformFormDataToRequestData, handleSuccessfulUpdate }: UseUpdateAccountHandlerProps<T>) => {
  const { user } = useSelector(getUserData)
  const [loading, setLoading] = useMountedState<boolean>(false)
  const [password, setPassword] = useState<string>('')
  const [serverErrors, setServerErrors] = useMountedState<UpdateAccountServerErrors>({})
  const flags = useFlags()
  const dispatch = useDispatch()

  const handleSubmit = useCallback(async (formData: T) => {
    setServerErrors({})

    // This should not happen, but cleans up the typescript and handles
    // if auth is ever messed up
    if (!user?.username) {
      Logger.error(
        'Account settings update modal submit called when username is not set!'
      )
      setServerErrors({ basic: true })
      return
    }

    setLoading(true)
    try {
      const updates = transformFormDataToRequestData(formData)
      if (flags.useUpdateAccountCookieForAccountUpdates) {
        await slsClient.updateAccount(updates)
      } else {
        await slsClient.updateAccountDetails({
          username: user.username,
          password,
          updates
        })
      }

      handleSuccessfulUpdate(dispatch, formData)
      trackSLSActivity('Success')
      onClose?.()
    } catch (error: unknown) {
      Logger.error('Failed to update account', error)
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          setServerErrors({ basic: true })
          return
        }
        trackSLSActivity('Failed')
        const { status } = error.response

        if (status === 400) {
          setServerErrors({ invalidCriteria: true })
        } else if (status === 401) {
          setServerErrors({ invalidCriteria: true })

          // This will cause the component to redisplay the password validation form
          setPassword('')
        } else if (status === 500) {
          setServerErrors({ basic: true })
        } else {
          // Catch any other unexpected error codes
          setServerErrors({ basic: true })
        }
      } else {
        // Catch any other unexpected error codes
        setServerErrors({ basic: true })
      }
    }
    setLoading(false)
  }, [setLoading, setPassword, setServerErrors, trackSLSActivity, onClose, dispatch, handleSuccessfulUpdate, transformFormDataToRequestData, user?.username, password, flags])

  return {
    handleSubmit,
    loading,
    serverErrors,
    user,
    password,
    setPassword
  }
}
